/**
 * SUPPORT-ONLY. Compact read-out of a charging session combining data from the
 * receipt (cost breakdown, hardware, operator, connector) and the linked
 * transaction (driver email). Replaces ReceiptSummaryCard.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Session } from '../../../../stores/types';
import { Card } from '../../../_ui';
import { fmtDur, fmtMoney, fmtQtyAtRate } from '../receipt/billingBand';
import { ReceiptInfo, ReceiptSummary } from '../receipt/receiptParser';

interface SessionSummaryCardProps {
  summary: ReceiptSummary;
  receiptInfo?: ReceiptInfo | null;
  transactionData?: Session | null;
  receiptUrl?: string;
}

const fmtDate = (d: Date, tz?: string): string =>
  d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: tz ?? undefined,
  });

const fmtTime = (d: Date, tz?: string): string =>
  d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz ?? undefined,
  });

const linkCls = 'text-denim hover:opacity-80';

interface FieldItem {
  label: string;
  value?: string;
  node?: React.ReactNode;
}

export const SessionSummaryCard = ({
  summary,
  receiptInfo,
  transactionData,
  receiptUrl,
}: SessionSummaryCardProps) => {
  const { t } = useTranslation();

  const { companyId, deviceId, timeZone: tz } = summary;
  const chargerLink =
    companyId && deviceId ? `/chargers/${deviceId}?companyId=${companyId}` : undefined;
  const companyLink = companyId ? `/overview?companyId=${companyId}` : undefined;

  const userEmail = transactionData?.userEmail;
  const sessionStart = receiptInfo?.sessionStart;
  const sessionEnd = receiptInfo?.sessionEnd;

  const equipmentFields: FieldItem[] = [
    {
      label: t('ocpp_timelog.receipt.operator'),
      node:
        summary.operator == null ? undefined : companyLink ? (
          <Link to={companyLink} className={linkCls}>
            {summary.operator}
          </Link>
        ) : (
          <span>{summary.operator}</span>
        ),
    },
    {
      label: t('ocpp_timelog.receipt.location'),
      value: summary.location ?? summary.address,
    },
    {
      label: t('ocpp_timelog.receipt.charger'),
      node: (() => {
        const name = summary.charger ?? summary.deviceId;
        if (!name) return undefined;
        return chargerLink ? (
          <Link to={chargerLink} className={linkCls}>
            {name}
          </Link>
        ) : (
          <span>{name}</span>
        );
      })(),
    },
    {
      label: t('ocpp_timelog.receipt.hardware'),
      value: [summary.vendor, summary.model].filter(Boolean).join(' ') || undefined,
    },
    {
      label: t('ocpp_timelog.receipt.connector'),
      value:
        [summary.connectorType, summary.connectorLocation].filter(Boolean).join(' · ') || undefined,
    },
  ].filter((f) => f.value != null || f.node != null);

  const timingFields: FieldItem[] = [
    {
      label: t('ocpp_timelog.receipt.started_via'),
      value: summary.startedVia,
    },
    {
      label: t('ocpp_timelog.receipt.user_email'),
      value: userEmail,
    },
    {
      label: t('ocpp_timelog.receipt.date'),
      value: sessionStart ? fmtDate(sessionStart, tz) : summary.when,
    },
    {
      label: t('ocpp_timelog.receipt.start_time'),
      value: sessionStart ? fmtTime(sessionStart, tz) : undefined,
    },
    {
      label: t('ocpp_timelog.receipt.end_time'),
      value: sessionEnd ? fmtTime(sessionEnd, tz) : undefined,
    },
    {
      label: t('ocpp_timelog.receipt.charging_time'),
      value: fmtDur(summary.chargingTimeSeconds),
    },
    {
      label: t('ocpp_timelog.receipt.plugged_in_time'),
      value: fmtDur(summary.pluggedInTimeSeconds),
    },
    {
      label: t('ocpp_timelog.receipt.idle_time'),
      value: fmtDur(summary.idleTimeSeconds),
    },
  ].filter((f: FieldItem) => f.value != null || f.node != null);

  const billingFields: FieldItem[] = [
    {
      label: t('ocpp_timelog.receipt.energy'),
      value: summary.energyKwh != null ? `${summary.energyKwh.toFixed(2)} kWh` : undefined,
    },
    {
      label: t('ocpp_timelog.receipt.total'),
      value: fmtMoney(summary.total, summary.currency),
    },
    {
      label: t('ocpp_timelog.receipt.transaction_id'),
      value: summary.transactionId,
    },
    {
      label: t('ocpp_timelog.receipt.receipt_id'),
      node: receiptInfo?.receiptId ? (
        receiptUrl ? (
          <a href={receiptUrl} target='_blank' rel='noopener noreferrer' className={linkCls}>
            {receiptInfo.receiptId}
          </a>
        ) : (
          <span>{receiptInfo.receiptId}</span>
        )
      ) : undefined,
    },
  ].filter((f) => f.value != null || f.node != null);

  const lineItems = summary.lineItems.filter(
    (li) => li.startTimeMs != null || li.total != null || li.energyKwh != null,
  );

  const hasContent =
    equipmentFields.length > 0 ||
    timingFields.length > 0 ||
    billingFields.length > 0 ||
    lineItems.length > 0;

  if (!hasContent) return null;

  const renderFieldGroup = (fields: FieldItem[]) =>
    fields.length > 0 ? (
      <dl className='grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3'>
        {fields.map((f) => (
          <div key={f.label} className='flex flex-col'>
            <dt className='text-xs text-grey5'>{f.label}</dt>
            <dd className='text-sm text-black'>{f.node ?? f.value}</dd>
          </div>
        ))}
      </dl>
    ) : null;

  return (
    <Card>
      <div className='flex flex-col gap-4 p-4'>
        <h3 className='text-base font-semibold text-black'>{t('ocpp_timelog.receipt.title')}</h3>

        {renderFieldGroup(equipmentFields)}

        {timingFields.length > 0 && (
          <>
            <div className='border-t border-grey2' />
            {renderFieldGroup(timingFields)}
          </>
        )}

        {(billingFields.length > 0 || lineItems.length > 0) && (
          <>
            <div className='border-t border-grey2' />
            {renderFieldGroup(billingFields)}
            {lineItems.length > 0 && (
              <div className='flex flex-col gap-2'>
                <h4 className='text-sm font-semibold text-black'>
                  {t('ocpp_timelog.receipt.line_items')}
                </h4>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-grey2 text-xs text-grey5'>
                        <th className='pb-1 text-left font-normal'>
                          {t('ocpp_timelog.receipt.item_type')}
                        </th>
                        <th className='pb-1 text-left font-normal'>
                          {t('ocpp_timelog.receipt.item_qty_rate')}
                        </th>
                        <th className='pb-1 text-right font-normal'>
                          {t('ocpp_timelog.receipt.item_total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li, i) => (
                        <tr key={i} className='border-b border-grey1'>
                          <td className='py-1 text-black'>{li.label}</td>
                          <td className='py-1 text-grey5'>{fmtQtyAtRate(li, summary.currency)}</td>
                          <td className='py-1 text-right text-black'>
                            {fmtMoney(li.total, summary.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
